import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Avatar, Box, Typography } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { buildCompanyUrl } from '../../../../helpers/urlHelpers';

const useStyles = makeStyles((theme) => ({
  carouselContainer: {
    width: '100%',
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3, 0),
    overflow: 'hidden',
    position: 'relative',
    background: `linear-gradient(135deg, ${theme.palette.background.paper}00 0%, ${theme.palette.background.paper}80 50%, ${theme.palette.background.paper}00 100%)`,
    borderRadius: theme.spacing(2),
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '15%',
      height: '100%',
      background: `linear-gradient(90deg, ${theme.palette.background.default} 0%, ${theme.palette.background.default}80 40%, transparent 100%)`,
      zIndex: 2,
      pointerEvents: 'none'
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      width: '15%',
      height: '100%',
      background: `linear-gradient(270deg, ${theme.palette.background.default} 0%, ${theme.palette.background.default}80 40%, transparent 100%)`,
      zIndex: 2,
      pointerEvents: 'none'
    }
  },
  headerSection: {
    textAlign: 'center',
    marginBottom: theme.spacing(2),
    position: 'relative',
    zIndex: 3
  },
  title: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1)
  },
  sparkle: {
    color: theme.palette.primary.main,
    animation: '$pulse 2s ease-in-out infinite'
  },
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 1,
      transform: 'scale(1)'
    },
    '50%': {
      opacity: 0.6,
      transform: 'scale(0.95)'
    }
  },
  carouselWrapper: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%'
  },
  carouselTrack: {
    display: 'flex',
    gap: theme.spacing(4),
    animation: '$scroll 50s linear infinite',
    width: 'fit-content',
    paddingLeft: '10%',
    paddingRight: '10%',
    '&:hover': {
      animationPlayState: 'paused'
    }
  },
  '@keyframes scroll': {
    '0%': {
      transform: 'translateX(10%)'
    },
    '100%': {
      transform: 'translateX(-40%)'
    }
  },
  logoWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-5px) scale(1.05)'
    },
    '&:hover $logoAvatar': {
      boxShadow: `0 12px 28px ${theme.palette.primary.main}30`,
      borderColor: theme.palette.primary.main
    }
  },
  logoAvatar: {
    width: 64,
    height: 64,
    backgroundColor: theme.palette.background.paper,
    border: `3px solid ${theme.palette.divider}`,
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    transition: 'all 0.3s ease',
    fontSize: '1.5rem',
    fontWeight: 700,
    color: theme.palette.primary.main,
    [theme.breakpoints.down('sm')]: {
      width: 52,
      height: 52,
      fontSize: '1.25rem'
    }
  },
  companyName: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    maxWidth: 80,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    opacity: 0.8,
    transition: 'opacity 0.2s ease'
  },
  emptyState: {
    display: 'none' // Hide when no logos
  }
}));

const CompanyLogosCarousel = ({ jobResults }) => {
  const classes = useStyles();
  const history = useHistory();

  // Navigate to company page
  const handleCompanyClick = (companyId, companyName) => {
    if (companyId) {
      history.push(buildCompanyUrl(companyId, companyName));
    }
  };

  // Extract unique companies with logos from job results
  const uniqueCompanies = useMemo(() => {
    if (!jobResults || jobResults.length === 0) {
      return [];
    }

    const companiesMap = new Map();

    jobResults.forEach((jobResult) => {
      const { company, file } = jobResult;
      // Logo is in file.publicUrl
      const logoUrl = file?.publicUrl;

      if (company && logoUrl && company.name) {
        // Use company id or name as key to avoid duplicates
        const key = company.id || company.name;
        if (!companiesMap.has(key)) {
          companiesMap.set(key, {
            id: company.id,
            name: company.name,
            logoUrl: logoUrl
          });
        }
      }
    });

    return Array.from(companiesMap.values());
  }, [jobResults]);

  // Get initials for fallback avatar
  const getInitials = (name) => {
    if (!name) return '?';
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Don't render if no companies with logos
  if (uniqueCompanies.length < 1) {
    return null;
  }

  // Duplicate the list for seamless infinite scroll
  const duplicatedCompanies = [...uniqueCompanies, ...uniqueCompanies];

  return (
    <Box className={classes.carouselContainer}>
      <Box className={classes.headerSection}>
        <Typography className={classes.title}>
          <span className={classes.sparkle}>✦</span>
          Used by remote-first companies hiring globally
          <span className={classes.sparkle}>✦</span>
        </Typography>
      </Box>
      <Box className={classes.carouselWrapper}>
        <Box className={classes.carouselTrack}>
          {duplicatedCompanies.map((company, index) => (
            <Box
              key={`${company.id || company.name}-${index}`}
              className={classes.logoWrapper}
              onClick={() => handleCompanyClick(company.id, company.name)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) =>
                e.key === 'Enter' &&
                handleCompanyClick(company.id, company.name)
              }>
              <Avatar
                className={classes.logoAvatar}
                src={company.logoUrl}
                alt={`${company.name} logo`}>
                {getInitials(company.name)}
              </Avatar>
              <Typography className={classes.companyName}>
                {company.name}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

CompanyLogosCarousel.propTypes = {
  jobResults: PropTypes.arrayOf(
    PropTypes.shape({
      company: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        logoUrl: PropTypes.string
      })
    })
  )
};

CompanyLogosCarousel.defaultProps = {
  jobResults: []
};

export default CompanyLogosCarousel;
